import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Church {
  id: string;
  name: string;
  description: string;
  location: string;
  pastor_name: string;
  contact_phone: string;
  contact_email: string;
  is_approved: boolean;
  created_at: string;
  image_url?: string;
}

export default function CommunityPage() {
  const [selectedTab, setSelectedTab] = useState('전체');
  const [churches, setChurches] = useState<Church[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user, profile } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    pastor_name: '',
    contact_phone: '',
    contact_email: '',
    website: '',
    established_year: '',
    denomination: ''
  });

  const tabs = ['전체', '내 교회', '인기', '최신'];

  useEffect(() => {
    loadChurches();
  }, []);

  const loadChurches = async () => {
    try {
      const { data, error } = await supabase
        .from('churches')
        .select('*')
        .eq('is_approved', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setChurches(data || []);
    } catch (error) {
      console.error('교회 목록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToChurch = (churchId: string) => {
    window.REACT_APP_NAVIGATE(`/community/church/${churchId}`);
  };

  const recentPosts = [
    {
      id: 1,
      title: '이번 주 수요예배 안내',
      community: '서울중앙교회',
      author: '김집사',
      time: '2시간 전',
      comments: 12,
      likes: 34
    },
    {
      id: 2,
      title: '청년부 성경공부 모임',
      community: '부산제일교회',
      author: '이전도사',
      time: '4시간 전',
      comments: 8,
      likes: 23
    },
    {
      id: 3,
      title: '주일학교 봉사자 모집',
      community: '대전은혜교회',
      author: '박권사',
      time: '6시간 전',
      comments: 15,
      likes: 45
    },
    {
      id: 4,
      title: '찬양팀 연습 일정 공지',
      community: '인천사랑교회',
      author: '최집사',
      time: '8시간 전',
      comments: 6,
      likes: 18
    }
  ];

  const handleCreateChurch = () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      window.REACT_APP_NAVIGATE('/auth/login');
      return;
    }
    setShowCreateModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    setSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('churches')
        .insert([
          {
            ...formData,
            created_by: user.id,
            is_approved: false
          }
        ])
        .select()
        .single();

      if (error) throw error;

      alert('교회 커뮤니티 생성 신청이 완료되었습니다.\n관리자 승인 후 이용 가능합니다.');
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        location: '',
        pastor_name: '',
        contact_phone: '',
        contact_email: '',
        website: '',
        established_year: '',
        denomination: ''
      });
      loadChurches();
    } catch (error) {
      console.error('교회 생성 실패:', error);
      alert('교회 커뮤니티 생성에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">교회별 커뮤니티</h1>
            <p className="text-lg text-gray-600">우리 교회만의 전용 공간에서 교인들과 소통하세요</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
                  <input
                    type="text"
                    placeholder="교회 검색..."
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
              </div>
              <button 
                onClick={handleCreateChurch}
                className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line mr-2"></i>
                커뮤니티 만들기
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                className={`px-5 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  selectedTab === tab
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {churches.map((church, index) => (
                    <div
                      key={church.id}
                      onClick={() => navigateToChurch(church.id)}
                      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer"
                    >
                      <div className="flex flex-col md:flex-row">
                        <div className="relative w-full md:w-64 h-48 overflow-hidden flex-shrink-0">
                          <img
                            src={church.image_url || `https://public.readdy.ai/ai/img_res/604b07f3e855adbd49664b4aa5e94c8d.jpg${church.id}-${index}&orientation=landscape`}
                            alt={church.name}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-6 flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                            {church.name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {church.description}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                            <div className="flex items-center space-x-1">
                              <i className="ri-map-pin-line"></i>
                              <span>{church.location}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <i className="ri-user-line"></i>
                              <span>{church.pastor_name}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <i className="ri-team-line"></i>
                                <span>교인 수</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <i className="ri-article-line"></i>
                                <span>활동 중</span>
                              </div>
                            </div>
                            <button className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                              입장하기
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {churches.length === 0 && !loading && (
                    <div className="text-center py-12">
                      <i className="ri-church-line text-4xl text-gray-300 mb-4"></i>
                      <p className="text-gray-500">등록된 교회가 없습니다.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-6">최근 게시글</h3>
                <div className="space-y-4">
                  {recentPosts.map((post) => (
                    <div
                      key={post.id}
                      className="pb-4 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                    >
                      <h4 className="font-semibold text-gray-900 mb-2 hover:text-teal-600 transition-colors">
                        {post.title}
                      </h4>
                      <p className="text-xs text-gray-500 mb-2">{post.community}</p>
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{post.author} · {post.time}</span>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <i className="ri-chat-3-line"></i>
                            <span>{post.comments}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <i className="ri-heart-line"></i>
                            <span>{post.likes}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 교회 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">교회 커뮤니티 만들기</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    교회명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="예: 서울중앙교회"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    교회 소개 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    placeholder="교회를 소개해주세요"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      교단 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="denomination"
                      value={formData.denomination}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                    >
                      <option value="">선택해주세요</option>
                      <option value="예장통합">예장통합</option>
                      <option value="예장합동">예장합동</option>
                      <option value="예장고신">예장고신</option>
                      <option value="기장">기장</option>
                      <option value="기감">기감</option>
                      <option value="성결교">성결교</option>
                      <option value="침례교">침례교</option>
                      <option value="순복음">순복음</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      설립연도
                    </label>
                    <input
                      type="text"
                      name="established_year"
                      value={formData.established_year}
                      onChange={handleInputChange}
                      placeholder="예: 1990"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    주소 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    placeholder="예: 서울특별시 강남구 테헤란로 123"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    담임목사 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pastor_name"
                    value={formData.pastor_name}
                    onChange={handleInputChange}
                    required
                    placeholder="예: 김목사"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      연락처 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      required
                      placeholder="예: 02-1234-5678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      이메일 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      required
                      placeholder="예: church@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    홈페이지
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="예: https://www.church.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-information-line text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold mb-1">안내사항</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>교회 커뮤니티는 관리자 승인 후 이용 가능합니다.</li>
                        <li>정확한 정보를 입력해주세요.</li>
                        <li>승인까지 1-2일 정도 소요될 수 있습니다.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? '생성 중...' : '생성하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
